'use client'

import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

type Language = 'en' | 'bm' | 'zh'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string, enText: string, bmText?: string, zhText?: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  // Always start with 'en' to ensure consistent SSR/hydration
  const [language, setLanguage] = useState<Language>('en')

  // Sync with localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    const saved = localStorage.getItem('language') as Language
    if (saved === 'en' || saved === 'bm' || saved === 'zh') {
      setLanguage(saved)
    }
  }, [])

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang)
    localStorage.setItem('language', lang)
  }

  const t = (_key: string, enText: string, bmText?: string, zhText?: string) => {
    if (language === 'bm' && bmText) {
      return bmText
    }
    if (language === 'zh' && zhText) {
      return zhText
    }
    return enText
  }

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage: handleSetLanguage, t }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
